import { mergeAttributes } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import FontSize from "tiptap-extension-font-size";
import TextStyle from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";

import Underline from "@tiptap/extension-underline";
import { Color } from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Highlight from "@tiptap/extension-highlight";

export const LINK_TYPE_FILE = "File";
export const LINK_TYPE_ATTRIBUTE = "data-link-type";

const DEFAULT_ALLOWED_URI_PATTERN =
  /^(?:(?:https?|ftp|ftps|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z0-9+.-]+(?:[^a-z+.-:]|$))/i;

function isBuilderRelativeHref(href: string): boolean {
  return (
    href.startsWith("/") ||
    href.startsWith("./") ||
    href.startsWith("../")
  );
}

function isDefaultAllowedHref(href: string): boolean {
  return DEFAULT_ALLOWED_URI_PATTERN.test(href);
}

const BuilderLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      dataLinkType: {
        default: null,
        parseHTML: (element) => element.getAttribute(LINK_TYPE_ATTRIBUTE),
        renderHTML: (attributes) => {
          if (!attributes.dataLinkType) {
            return {};
          }

          return {
            [LINK_TYPE_ATTRIBUTE]: attributes.dataLinkType,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `a[${LINK_TYPE_ATTRIBUTE}="${LINK_TYPE_FILE}"]`,
        getAttrs: (element) => {
          const el = element as HTMLElement;
          const href = (el.getAttribute("href") || "").trim();
          if (!href) {
            return false;
          }

          return {
            href,
            target: el.getAttribute("target") || "_blank",
            rel: el.getAttribute("rel") || "noopener noreferrer",
            dataLinkType: LINK_TYPE_FILE,
          };
        },
      },
      ...(this.parent?.() || []),
    ];
  },

  renderHTML({ mark, HTMLAttributes }) {
    const href = (HTMLAttributes.href || "").trim();

    if (HTMLAttributes.dataLinkType === LINK_TYPE_FILE && href) {
      return [
        "a",
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
        0,
      ];
    }

    const parentRender = this.parent?.({ mark, HTMLAttributes });
    if (parentRender) {
      return parentRender;
    }

    return [
      "a",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },
});

/**
 * FontSize - Custom Extension
 * editor.commands.setFontSize(e.target.value) :set the font-size.
 */

// --------- add this block ---- vvvvvv ----------
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      /**
       * Set the font size
       */
      setFontSize: (size: string) => ReturnType;
      /**
       * Unset the font size
       */
      unsetFontSize: () => ReturnType;
    };
  }
}
export const extensions = [
  StarterKit,
  FontSize,
  TextStyle,
  TextAlign.configure({
    types: ["heading", "paragraph"],
  }),
  FontFamily,
  Underline,
  Color,
  BuilderLink.configure({
    openOnClick: false,
    autolink: true,
    HTMLAttributes: {
      rel: "noopener noreferrer",
    },
    validate: (url) => {
      const href = (url || "").trim();
      if (!href) {
        return false;
      }

      if (isBuilderRelativeHref(href)) {
        return true;
      }

      return isDefaultAllowedHref(href);
    },
  }),
  Subscript,
  BulletList,
  OrderedList,
  Highlight.configure({
    multicolor: true,
    HTMLAttributes: {
      class: "highlight",
    },
  }),
  ListItem,
  Superscript,
];
